import { Alert, Button, FormField, Modal, Select, SelectItem, Tabs } from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useEmailsDaSelecao,
  useIdiomasDoFiltro,
  useIniciarRodada,
  useInvalidarFase,
  usePreviaConvite,
} from '../hooks';
import { IDIOMAS } from '../hooks/useFiltrosParticipantes';
import type { Idioma, ItemPainel, SelecaoLote } from '../types/Painel';
import { ListaNomes } from './ConfirmarLoteDialog';
import { identificacao } from './colunas';
import { PreviaAoVivo } from './PreviaAoVivo';

const CADA_UM = 'cada';
const MAX_NOMES = 10;

type Props = {
  campanhaId: string;
  /** Quantas pessoas recebem (R16). */
  publico: number;
  /** Quem recebe: dá a rodada, os idiomas das abas (R16) e quem não tem e-mail (spec 31 R19). */
  selecao: SelecaoLote;
  /** Os itens da seleção por ids (nomes, spec 25 R5; idiomas, R16); vazio na seleção por filtro. */
  itens?: readonly ItemPainel[];
  /** "N sem e-mail" leva à lista dessas pessoas (spec 31 R19); sem ele, o botão não existe. */
  onVerSemEmail?: () => void;
  /** A rodada começou. */
  onEnviado?: () => void;
  onFechar: () => void;
  /** "Cancelar"; sem ele, fecha (no diálogo "Lembrar", volta aos cartões: spec 31 R4). */
  onCancelar?: () => void;
};

/** Spec 30 R16 e R17: o público, o idioma e o e-mail em tamanho real antes de enviar. */
export function EnviarConviteModal({
  open,
  ...props
}: Omit<Props, 'onCancelar'> & { open: boolean }) {
  const { t } = useTranslation(['convites', 'common']);
  return (
    <Modal open={open} onClose={props.onFechar} size="2xl" ariaLabel={t('convites:dialogo.titulo')}>
      <Modal.Header
        title={t('convites:dialogo.titulo')}
        onClose={props.onFechar}
        dismissLabel={t('common:close')}
      />
      {open && <EnviarConviteConteudo {...props} />}
    </Modal>
  );
}

/** Corpo e rodapé do envio, para o `Modal` acima e para o diálogo "Lembrar" (spec 31 R4). */
export function EnviarConviteConteudo({
  campanhaId,
  publico,
  selecao,
  itens = [],
  onVerSemEmail,
  onEnviado,
  onFechar,
  onCancelar = onFechar,
}: Props) {
  const { t } = useTranslation(['convites', 'participantes', 'common', 'erros']);
  const rodada = useIniciarRodada(campanhaId);
  const invalidarFase = useInvalidarFase(campanhaId);
  const [escolha, setEscolha] = useState<Idioma | typeof CADA_UM>(CADA_UM);
  const [aba, setAba] = useState<Idioma | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // Os idiomas do público, não os da campanha: por ids, dos itens; por filtro, do filtro (R16).
  const filtro = 'filtro' in selecao ? selecao.filtro : null;
  const doFiltro = useIdiomasDoFiltro(campanhaId, filtro);
  const idiomas = filtro
    ? doFiltro
    : Object.fromEntries(IDIOMAS.map((i) => [i, itens.filter((p) => p.idioma === i).length]));
  // Com "o idioma de cada pessoa", uma aba por idioma presente, com a contagem (R16).
  const presentes = IDIOMAS.filter((i) => idiomas[i] > 0);
  const comAbas = escolha === CADA_UM && presentes.length > 1;

  // Quem não tem e-mail vai para `falha`: o modal avisa antes (spec 31 R19). O "Lembrar" já tira
  // essas pessoas do público (R4), e lá a rota não é chamada de novo.
  const soComEmail = filtro?.temEmail?.length === 1 && filtro.temEmail[0] === 'sim';
  const emails = useEmailsDaSelecao(
    campanhaId,
    `envio:${JSON.stringify(selecao)}`,
    selecao,
    !soComEmail,
    () => {},
  );
  const semEmail = emails.data?.semEmail ?? 0;
  const idiomaPrevia: Idioma =
    escolha !== CADA_UM ? escolha : aba && presentes.includes(aba) ? aba : (presentes[0] ?? 'PT');
  const previa = usePreviaConvite(campanhaId, undefined, true, idiomaPrevia);

  function fechar() {
    setErro(null);
    onFechar();
  }

  async function enviar() {
    setErro(null);
    const codigo = await rodada.iniciar({
      modo: 'selecao',
      selecao,
      idioma: escolha === CADA_UM ? undefined : escolha,
    });
    switch (codigo) {
      case null:
        onEnviado?.();
        return fechar();
      case 'SELECAO_DESATUALIZADA':
        // O número vem do progresso ou da lista: refeitos, o modal mostra o novo (R17).
        void invalidarFase();
        return setErro(t(`erros:${codigo}`));
      case 'SELECAO_VAZIA':
      case 'CAMPANHA_SEM_CONVITE':
        void invalidarFase();
        return fechar();
      case 'RODADA_EM_ANDAMENTO':
        return fechar();
      default:
        setErro(t(`erros:${codigo}`));
    }
  }

  return (
    <>
      <Modal.Body>
        <div className="flex flex-col gap-4 text-sm">
          <p className="font-medium" data-envio-publico>
            {semEmail >= 1
              ? t('convites:envio.resumo', {
                  pessoas: t('convites:envio.publico', { count: publico }),
                  receberao: t('convites:envio.receberao', { count: publico - semEmail }),
                })
              : t('convites:envio.publico', { count: publico })}
          </p>
          {semEmail >= 1 && onVerSemEmail && (
            <Button
              variant="secondary"
              size="sm"
              className="self-start"
              onClick={() => {
                fechar();
                onVerSemEmail();
              }}
            >
              {t('convites:lembrar.semEmail', { count: semEmail })}
            </Button>
          )}
          <ListaNomes nomes={itens.map(identificacao)} max={MAX_NOMES} />
          <FormField label={t('convites:idioma.rotulo')}>
            <Select value={escolha} onValueChange={(v) => setEscolha(v as Idioma | typeof CADA_UM)}>
              <SelectItem value={CADA_UM}>{t('convites:idioma.cadaUm')}</SelectItem>
              {IDIOMAS.map((i) => (
                <SelectItem key={i} value={i}>
                  {t(`participantes:idiomas.${i}`)}
                </SelectItem>
              ))}
            </Select>
          </FormField>
          {comAbas && (
            <Tabs value={idiomaPrevia} onValueChange={(v) => setAba(v as Idioma)}>
              <Tabs.List>
                {presentes.map((i) => (
                  <Tabs.Trigger key={i} value={i}>
                    {i} {idiomas[i]}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
            </Tabs>
          )}
          <PreviaAoVivo previa={previa.data} atualizando={previa.isFetching} />
          {erro && (
            <Alert variant="danger" role="alert">
              {erro}
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancelar}>
          {t('common:cancel')}
        </Button>
        <Button variant="primary" loading={rodada.isPending} onClick={enviar}>
          {t('convites:dialogo.enviar')}
        </Button>
      </Modal.Footer>
    </>
  );
}
